import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import owlyLogo from "../../assets/owly-logo.png";
import owlyLogoSvg from "../../assets/logo.svg";
import "./Sidebar.css";
import navigation from "../../navigation";
import { logoutUser } from '../../../Authn/Authenticator';
import ButtonClose from "../Buttons/ButtonClose/ButtonClose";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import { Info } from "lucide-react";
import useUser from "../../../hooks/useUser";
import { hasAdminAccess } from "../../../utils/authz";

// Allowed items for non-admin users
const STUDENT_ALLOWED = ["Dashboard", "My Subscription", "My Profile", "Chat with Owly", "Logout"];

export default function Sidebar({ isOpen, onClose }) {
  const { user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('sidebar');
  const [aboutOpen, setAboutOpen] = React.useState(false);
  const versionText = t("common:appVersion", { version: process.env.REACT_APP_TAG_VERSION || "dev" });

  // Función de logout que usa la función manual del App para evitar SSO automático
  async function handleLogout() {
    // Usar la función de logout manual del App si está disponible
    if (typeof window !== "undefined" && window.owlyManageManualLogout) {
      window.owlyManageManualLogout();
      navigate("/");
      return;
    }

    // Fallback a la implementación original si no está disponible
    
    // Clear storages
    try {
      if (typeof window !== "undefined") {
        if (window.localStorage) {
          localStorage.clear();
        }
        if (window.sessionStorage) {
          sessionStorage.clear();
        }
        // Clear all cookies for current path
        if (document && document.cookie) {
          const cookies = document.cookie.split(";");
          for (const c of cookies) {
            const eqPos = c.indexOf("=");
            const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
            // Expire cookie for common paths
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${window.location.pathname}`;
          }
        }
      }
    } catch (e) {
      // no-op, proceed with logout
      console.warn("Storage/cookie clear failed", e);
    }
    const setUser = user && user.setUser ? user.setUser : null;
    const token = user && (user.access || user.token || (user.tokens && user.tokens.access))
      ? (user.access || user.token || (user.tokens && user.tokens.access))
      : undefined;
    await logoutUser({ setUser, token });
    navigate("/");
  }

  // Filter navigation items based solely on user's role_id.have_admin_access
  const filteredNavigation = React.useMemo(() => {
    if (!hasAdminAccess(user)) {
      return navigation.filter(item => STUDENT_ALLOWED.includes(item.name));
    }
    return navigation;
  }, [user]);

  return (
    <>
      {/* About Modal (ConfirmModal style) */}
      <ConfirmModal
        isOpen={aboutOpen}
        title={t('aboutTitle')}
        message={versionText}
        showFooter={false}
        bodyCenter={true}
        showCloseIcon={true}
        onConfirm={() => setAboutOpen(false)}
        onCancel={() => setAboutOpen(false)}
      />
      {/* Mobile backdrop */}
      {isOpen && (
        <div className="owly-sidebar-backdrop lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar (drawer en mobile, fijo en desktop) */}
      <div
        className={`owly-sidebar fixed z-50 top-0 left-0 h-full bg-white transition-transform duration-200 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:static lg:translate-x-0 lg:block`}
        style={{ width: 260, maxWidth: 260 }}
      >
        {/* Botón X solo en mobile */}
        <ButtonClose
          onClick={onClose}
          ariaLabel={t('closeAria')}
          size={28}
          className="button-close-sidebar"
          style={{
            background: "none",
            border: "none",
            fontSize: 28,
            color: "#888",
            zIndex: 200,
          }}
        />
        {/* Header */}
        <div className="owly-sidebar-header">
          <div className="owly-sidebar-logo-title">
            <img src={owlyLogo} alt={t('owlyLogoAlt')} className="owly-sidebar-logo" />
            <div>
              <h1 className="owly-sidebar-title">{t('title')}</h1>
              <p className="text-sm text-gray-500 no-margin">{t('subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="owly-sidebar-nav">
          {filteredNavigation.map((item) => {
            if (item.type === "logout") {
              return (
                <React.Fragment key="logout-group">
                  {/* About button inserted just before Logout */}
                  <button
                    key="About"
                    className="owly-sidebar-nav-link"
                    style={{ background: "transparent", border: "none", cursor: "pointer" }}
                    onClick={() => {
                      setAboutOpen(true);
                      onClose(); // Close the sidebar
                    }}
                  >
                    <Info size={20} />
                    <span className="owly-sidebar-nav-link-text">{t('about')}</span>
                  </button>
                  <button
                    key={item.name}
                    className="owly-sidebar-nav-link owly-sidebar-logout-btn"
                    style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer" }}
                    onClick={handleLogout}
                  >
                    <item.icon size={20} />
                    <span className="owly-sidebar-nav-link-text">{t(item.name)}</span>
                  </button>
                </React.Fragment>
              );
            }
            return (
              <NavLink
                key={item.name}
                to={`/app/${item.href.replace(/^\/+/, "")}`}
                end={item.href === "/"}
                className={({ isActive }) =>
                  "owly-sidebar-nav-link" + (isActive ? " active" : "")
                }
                onClick={() => {
                  if (item.name === "Chat with Owly") {
                    navigate('/app/chat');
                    if (window.innerWidth < 1024) { onClose?.(); }
                    return;
                  }
                  if (window.innerWidth < 1024) { onClose?.(); }
                }}
              >
                {item.name === "Chat with Owly" ? (
                  <img
                    src={owlyLogoSvg}
                    alt={t('owlyLogoAlt')}
                    style={{ width: 20, height: 20 }}
                  />
                ) : item.icon ? (
                  <item.icon size={20} />
                ) : null}
                <span className="owly-sidebar-nav-link-text">{t(item.name)}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="owly-sidebar-footer">
          <div className="owly-sidebar-avatar">
            {user && (user.name || user.username)
              ? (user.name || user.username).charAt(0)
              : "?"}
          </div>
          <div className="owly-sidebar-user-info">
            <p className="owly-sidebar-user-name">
              {user && (user.name || user.username) ? (user.name || user.username) : t('guest')}
            </p>
            <p className="owly-sidebar-user-email">
              {user && user.email ? user.email : t('noEmail')}
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
